import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from "axios";
import {
    Box,
    Button,
    Dialog,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Slide,
    TextField,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
    Alert,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { quantum, trio } from 'ldrs';
import { useNavigate } from "react-router-dom";
import { Notyf } from 'notyf';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';

const notyf = new Notyf({
    position: { x: 'left', y: 'bottom' },
    duration: 3000,
});


quantum.register();
trio.register();

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});


const MakePayment = ({ order }) => {
    const [open, setOpen] = useState(false);
    const [token, setToken] = useState(() => localStorage.getItem('token') || null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [memo, setMemo] = useState('');
    const [paymentId, setPaymentId] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [user, setUser] = useState({});
    const [editOpen, setEditOpen] = useState(false);
    const [editPayment, setEditPayment] = useState({});
    const [editMemo, setEditMemo] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');

    const filteredPayments = useMemo(() => {
    if (!order || !payments) return [];

    return payments
        .filter(payment => {
            const matchesOrderId = order.id === payment.payment_id;
            if (!matchesOrderId) return false;

            const matchesSearchQuery = searchQuery
                ? payment.pay_id.toLowerCase().includes(searchQuery.toLowerCase())
                : true;

            return matchesSearchQuery;
        })
        .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);

            if (dateA.getTime() !== dateB.getTime()) {
                return dateB.getTime() - dateA.getTime();
            }

            return b.id - a.id; 
        });

}, [payments, searchQuery, order]);


    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                navigate("/"); // Redirect if no token
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/"); // Redirect on 401 error
                }
            }
        };

        fetchProfile();
    }, []);

    useEffect(() => {
        if (user.id) {
            fetchPayments();
        }
    }, [user.id]);

    const handleSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };


    const handleEditOpen = (payment) => {
        setEditPayment(payment);
        setEditMemo(memo);
        setEditOpen(true);
    };

    const handleEditClose = () => {
        setEditOpen(false);
    };

    const handleEditSave = async () => {
        setLoading(true); // Start loading

        try {
            const paymentData = {
                payment_id: editPayment.payment_id,
                amount: editPayment.amount,
                memo: editPayment.memo,
                date: editPayment.date,
            };

            await axios.put(`https://admin.bizstor.lk/public/api/update-payment/${editPayment.id}`, paymentData);
            {/**console.log('Payment updated successfully:');*/ }

            // Fetch updated payments
            fetchPayments();
            //setAlertOpen(true);
            notyf.success('Payment updated successfull!');
            // Close the edit dialog after saving
            handleEditClose();
        } catch (error) {
            notyf.error('Error updating payment!');
        } finally {
            setLoading(false);
        }
    };

    const handleEditAmountChange = (event) => {
        setEditPayment({ ...editPayment, amount: event.target.value });
    };

    const handleEditMemoChange = (event) => {
        setEditPayment({ ...editPayment, memo: event.target.value });
    };

    const handleEditDateChange = (event) => {
        setEditPayment({ ...editPayment, date: event.target.value });
    };


    useEffect(() => {
        if (order) {
            setPaymentId(order.id.toString());
            fetchPayments();
        }
    }, [order]);

    if (!order) return null;

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handlePay = async () => {
        setLoading(true); // Start loading

        // Calculate total amount from the order details
        const totalAmount = calculateTotalAmount(order.items, order.discount);

        try {
            const paymentData = {
                // --- Existing Data ---
                usertoken: user.id,
                payment_id: paymentId,
                amount: paymentAmount,
                memo: memo,
                name: order.fullname,
                pay_id: `RCPT-${Date.now()}`,
                date: paymentDate || new Date().toISOString().split('T')[0],
                email: order.email,

                invoiceNo: order.invoiceNo,
                totalAmount: totalAmount,
            };

            // Send the updated data object to your backend
            await axios.post('https://admin.bizstor.lk/public/api/save-payment-data', paymentData);

            // --- Your existing success logic ---
            setPaymentAmount('');
            setMemo('');
            setPaymentDate('');
            notyf.success('Payment successful!');
            fetchPayments();

        } catch (error) {
            //console.error('Error processing payment:', error);
            notyf.error('Error processing payment!');
        } finally {
            setLoading(false); // Stop loading
        }
    };


    const handlePayAndClose = async () => {
        setLoading(true); // Start loading

        try {
            const paymentData = {
                usertoken: user.id,
                payment_id: paymentId,
                amount: paymentAmount,
                memo: memo,
                name: order.fullname,
                pay_id: `RCPT-${Date.now()}`,
                date: paymentDate || new Date().toISOString().split('T')[0],

                email: order.email,

                invoiceNo: order.invoiceNo,
                totalAmount: totalAmount,
            };

            await axios.post('https://admin.bizstor.lk/public/api/save-payment-data', paymentData);

            // Clear payment amount after payment
            setPaymentAmount('');
            setMemo('');
            setPaymentDate('');

            // Fetch updated payments
            fetchPayments();
            setAlertOpen(true);
            notyf.success('Payment successfull!');
            // Close the dialog after payment
            handleClose();
        } catch (error) {
            //console.error('Error processing payment:', error);
            notyf.error('Error processing payment!');
        } finally {
            setLoading(false); // Stop loading
        }
    };

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-payment-data');
            if (user.id) {
                const Payment = response.data.filter(payment =>
                    payment.usertoken &&
                    payment.usertoken.toLowerCase() === user.id.toString().toLowerCase()
                );
                setPayments(Payment);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };



    const deletePayment = async (paymentId) => {
        setLoading(true); // Start loading

        try {
            await axios.delete(`https://admin.bizstor.lk/public/api/delete-payment-data/${paymentId}`);
            {/**console.log('Payment deleted successfully:');*/ }
            //handleSnackbar('Payment deleted successfully!', 'success');
            notyf.success('Payment deleted successfull!');
            // Fetch updated payments
            fetchPayments();
            setAlertOpen(true);
        } catch (error) {
            //console.error('Error deleting payment:', error);
            notyf.error('Error deleting payment!');
        } finally {
            setLoading(false); // Stop loading
        }
    };

    const calculateTotalAmount = (items, discount) => {
        // Calculate the total amount considering item-specific taxes
        const totalAmount = items.reduce((acc, item) => {
            const itemTotal = Number(item.price) + (Number(item.price) * (item.tax / 100)); // Adding item-specific tax
            return acc + itemTotal;
        }, 0);

        // Apply discount
        const discountedAmount = totalAmount - (totalAmount * (discount / 100));

        // Calculate total amount
        return discountedAmount;
    };



    const handleAmountChange = (event) => {
        setPaymentAmount(event.target.value);
    };

    const handleMemoChange = (event) => {
        setMemo(event.target.value);
    };

    const handleDateChange = (event) => {
        setPaymentDate(event.target.value);
    };

    const dueAmount = calculateTotalAmount(order.items, order.discount) - Number(order.payamount);

    const calculateTotalPayments = () => {
        let total = 0;
        payments.forEach((payment) => {
            if (order.id === payment.payment_id) {
                total += Number(payment.amount);

            }
        });
        return total;
    };

    const getPaymentStatus = () => {
        const totalPaid = Number(order.payamount) + calculateTotalPayments();
        const totalAmount = calculateTotalAmount(order.items, order.discount);

        if (totalPaid === totalAmount) {
            return 'Paid'
        }
        if (totalPaid > totalAmount) {
            return 'Overpaid'
        } else {
            return 'Pending'
        }
    };

    const paymentStatus = getPaymentStatus();

    const totalAmount = calculateTotalAmount(order.items, order.discount);
    const totalPaid = (Number(order.payamount) || 0) + calculateTotalPayments();

const printPaymentReceipt = (paymentDetails) => {
    const totalPaid =
        paymentDetails.payOnReceipt +
        paymentDetails.otherPayments +
        paymentDetails.paymentAmount;

    const dueAmount = paymentDetails.totalAmount - totalPaid;

    const printWindow = window.open('', '_blank');
    const printContent = `
    <html>
        <head><title>Payment Receipt - #${order.invoiceNo}</title></head>
        <body style="font-family: Arial, sans-serif; margin: 20px;">
            <div style="max-width: 400px; margin: auto; padding: 20px; border: 1px solid #ccc; border-radius: 8px;">

                <!-- User Image -->
                <div style="text-align: center; margin-bottom: 10px;">
                    <img src=${`https://admin.bizstor.lk/storage/app/public/images/${user.image}`} alt="Logo" style="max-width: 100px; max-height: 100px; object-fit: contain;"/>
                    <div style="margin-top: 5px; font-weight: bold; color: #333; text-transform: uppercase;"><h2>${user.companyname ? user.companyname : ''}</h2></div>
                </div>

                <h4 style="text-align: center; color: #333;">Payment Receipt</h4>
                <p><strong>Invoice No:</strong> #${order.invoiceNo}</p>
                <p><strong>Receipt No:</strong> ${paymentDetails.payId}</p>
                <p><strong>Date:</strong> ${new Date(paymentDetails.date).toLocaleDateString()}</p>
                <hr/>
                <table style="width: 100%; margin-top: 15px; border-spacing: 0 5px;">
                    <tr>
                        <td>Total Amount:</td>
                        <td style="text-align: right;">LKR ${paymentDetails.totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    </tr>
                    <tr>
                        <td style="padding-top: 10px;">Initial Payment:</td>
                        <td style="text-align: right; padding-top: 10px;">LKR ${paymentDetails.payOnReceipt.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    </tr>
                    <tr>
                        <td>All Payments:</td>
                        <td style="text-align: right;">LKR ${paymentDetails.otherPayments.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    </tr>
                    <tr style="font-weight: bold;">
                        <td>This Payment:</td>
                        <td style="text-align: right;">LKR ${paymentDetails.paymentAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</td>
                    </tr>
                </table>
                <hr/>
                <div style="text-align: right; margin-top: 20px;">
                    <p style="margin: 0;"><strong>Total Paid:</strong> LKR ${totalPaid.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</p>
                    <p style="font-size: 1.2em; font-weight: bold; color: #d32f2f; margin-top: 5px;">
                        Due Amount: LKR ${dueAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    </p>
                </div>
            </div>
        </body>
    </html>`;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 1000);
};


    const handlePayAndPrint = async () => {
        if (!paymentAmount || Number(paymentAmount) <= 0) {
            notyf.error('Please enter a valid payment amount.');
            return;
        }

        setLoading(true);

        const paymentData = {
            usertoken: user.id,
            payment_id: order.id,
            amount: paymentAmount,
            memo: memo,
            name: order.fullname,
            pay_id: `RCPT-${Date.now()}`,
            date: paymentDate || new Date().toISOString().split('T')[0],

            email: order.email,

                invoiceNo: order.invoiceNo,
                totalAmount: totalAmount,
        };

        try {
            await axios.post('https://admin.bizstor.lk/public/api/save-payment-data', paymentData);

            notyf.success('Payment successful!');

            const receiptDetails = {
                totalAmount: totalAmount,
                payOnReceipt: Number(order.payamount) || 0,
                otherPayments: calculateTotalPayments(),
                paymentAmount: Number(paymentAmount),
                totalPaid: totalPaid + Number(paymentAmount),
                dueAmount: dueAmount - Number(paymentAmount),
                date: paymentData.date,
                payId: paymentData.pay_id,
            };

            printPaymentReceipt(receiptDetails);

            fetchPayments();
            setPaymentAmount('');
            setMemo('');
            setPaymentDate('');

        } catch (error) {
            console.error('Error processing payment:', error);
            notyf.error('Error processing payment!');
        } finally {
            setLoading(false);
        }
    };


const handlePrintFromHistory = (paymentToPrint) => {
    const allPaymentsForOrder = payments.filter(p => p.payment_id === order.id);

    const sortedPaymentsForOrder = allPaymentsForOrder.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);

        if (dateA.getTime() !== dateB.getTime()) {
            return dateA.getTime() - dateB.getTime(); // Ascending date order
        }
        return a.id - b.id; // Ascending ID order (for numbers)
    });

    const otherPaymentsAtTheTime = sortedPaymentsForOrder
        .filter(p => {
            const pDate = new Date(p.date);
            const printDate = new Date(paymentToPrint.date);

            if (pDate.getTime() < printDate.getTime()) {
                return true;
            }

            if (pDate.getTime() === printDate.getTime()) {
                return p.id < paymentToPrint.id; // Compare IDs as numbers
            }

            return false; // Otherwise, do not include
        })
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);


    const paymentDetails = {
        totalAmount: calculateTotalAmount(order.items, order.discount),
        payOnReceipt: parseFloat(order.payamount) || 0,
        otherPayments: otherPaymentsAtTheTime,
        paymentAmount: parseFloat(paymentToPrint.amount),
        date: paymentToPrint.date,
        payId: paymentToPrint.pay_id,
    };

    printPaymentReceipt(paymentDetails);
};



    return (
        <React.Fragment>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <Button variant="outlined" onClick={handleClickOpen} style={{ backgroundColor: 'black', color: 'white' }}>
                        Make Payment
                        <PaymentsIcon sx={{ marginLeft: '10px' }} />
                    </Button>
                </Box>
                <Dialog
                    fullScreen
                    open={open}
                    onClose={handleClose}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: 'fixed', bgcolor: '#FAFAFA', boxShadow: 'none' }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                                <CloseIcon sx={{ color: 'red' }} />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1, color: 'black', fontWeight: 'bold' }} variant="h6" component="div">
                                Make Payment 
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ p: 3, mt: 8 }}>
                        <Box>
                            <Typography
                                sx={{
                                    mb: 2,
                                    fontSize: '25px',
                                    fontWeight: 'bold',
                                    '@media (max-width: 600px)': {
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        mb: 4,
                                    },
                                }}>Payment for Invoice No : {order.invoiceNo} <br /> Status : {paymentStatus}
                            </Typography>
                            {/**<p><strong>Customer :</strong> {order.customer}</p>*/}
                            <p><strong>Total Amount :</strong> LKR.{calculateTotalAmount(order.items, order.discount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</p>
                            <p><strong>Initial Payment :</strong> LKR.{(order.payamount != null ? order.payamount : '0.00')}</p>
                            <p><strong>Previous Payments :</strong> LKR.{calculateTotalPayments().toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</p>
                            <p><strong>Total Payments :</strong> LKR.{(((Number(order.payamount) != null ? Number(order.payamount) : 0) + calculateTotalPayments()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ","))}</p>                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-end',
                                    justifyContent: 'flex-end',
                                    marginTop: '10px',
                                    textAlign: 'right',
                                }}
                            >
                                <Typography variant="subtitle1">Due Amount</Typography>
                                <Typography variant="h4" fontWeight="bold" sx={{ mb: 2 }}>
                                    LKR.{(dueAmount - calculateTotalPayments()).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                </Typography>
                            </Box>
                        </Box>


                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Make Payment</Typography>
                            <TableContainer >
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            {/**<TableCell>ID</TableCell>*/}
                                            <TableCell align="left">Date</TableCell>
                                            <TableCell align="left">Memo</TableCell>
                                            <TableCell align="left">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            {/**<TableCell component="th" scope="row">
                                                <TextField
                                                    value={paymentId}
                                                    disabled
                                                    size="small"
                                                    sx={{ width: '100%' }}
                                                />
                                            </TableCell>*/}
                                            <TableCell align="left">
                                                <TextField
                                                    type="date"
                                                    value={paymentDate || new Date().toISOString().split('T')[0]}
                                                    size="small"
                                                    onChange={handleDateChange}
                                                    sx={{
                                                        width: '100%',
                                                        '@media (max-width: 600px)': {
                                                            width: '90%',
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell align="left">
                                                <TextField
                                                    type="text"
                                                    value={memo}
                                                    size="small"
                                                    onChange={handleMemoChange}
                                                    sx={{
                                                        width: '100%',
                                                        '@media (max-width: 600px)': {
                                                            width: '90%',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="left">
                                                <TextField
                                                    type="number"
                                                    value={paymentAmount}
                                                    size="small"
                                                    onChange={handleAmountChange}
                                                    sx={{
                                                        width: '100%',
                                                        '@media (max-width: 600px)': {
                                                            width: '90%',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box display="flex" justifyContent="flex-end" mt={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: { xs: 'center', sm: 'flex-end' }, alignItems: 'center' }}>
                                <Button onClick={handlePay} variant="contained" color="primary" sx={{ width: { sm: '100px', xs: '100%' } }}>
                                    {loading ? (
                                        <Box sx={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            width: '100vw',
                                            height: '100vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'white',
                                            zIndex: 9999
                                        }}>
                                            <l-trio
                                                size="50"
                                                speed="1.3"
                                                color="red"
                                            ></l-trio>
                                        </Box>
                                    ) : 'Pay'}
                                </Button>
                                <Button onClick={handlePayAndPrint} variant="contained" color="primary" sx={{ width: { sm: '150px', xs: '100%' } }}>
                                    {loading ? (
                                        <Box sx={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            width: '100vw',
                                            height: '100vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'white',
                                            zIndex: 9999
                                        }}>
                                            <l-trio
                                                size="50"
                                                speed="1.3"
                                                color="red"
                                            ></l-trio>
                                        </Box>
                                    ) : 'Pay & Print'}
                                </Button>
                                <Button onClick={handlePayAndClose} variant="contained" color="error" sx={{ width: { sm: '150px', xs: '100%' } }}>
                                    {loading ? (
                                        <Box sx={{
                                            position: 'fixed',
                                            top: 0,
                                            left: 0,
                                            width: '100vw',
                                            height: '100vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'white',
                                            zIndex: 9999
                                        }}>
                                            <l-trio
                                                size="50"
                                                speed="1.3"
                                                color="red"
                                            ></l-trio>
                                        </Box>
                                    ) : 'Pay & Close'}
                                </Button>
                            </Box>
                        </Box>


                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Payment History</Typography>
                            <TextField
                                variant="outlined"
                                fullWidth
                                placeholder="Search by RCPT No"
                                size='small'
                                margin="normal"
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left">#</TableCell>
                                            <TableCell align="left">Date</TableCell>
                                            <TableCell align="left">Amount</TableCell>
                                            <TableCell align="left">Memo</TableCell>
                                            <TableCell align="right"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredPayments.map((payment, index) => (
                                            <TableRow key={payment.id}>
                                                <TableCell align="left">{payment.pay_id}</TableCell>
                                                <TableCell align="left">{payment.date}</TableCell>
                                                <TableCell align="left">LKR.{parseFloat(payment.amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</TableCell>
                                                <TableCell align="left">{payment.memo}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton onClick={() => handleEditOpen(payment)}>
                                                        <EditIcon sx={{ '&:hover': { color: 'blue' } }} />
                                                    </IconButton>

                                                    <IconButton onClick={() => handlePrintFromHistory(payment)}>
                                                        <PrintIcon sx={{ '&:hover': { color: 'green' } }} />
                                                    </IconButton>

                                                    <IconButton aria-label="delete" onClick={() => deletePayment(payment.id)} disabled={loading}>
                                                        <DeleteIcon sx={{ '&:hover': { color: 'red' } }} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Snackbar
                                open={snackbarOpen}
                                autoHideDuration={6000}
                                onClose={handleSnackbarClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                                <Alert
                                    onClose={handleSnackbarClose}
                                    severity={snackbarSeverity}
                                    sx={{
                                        width: '100%',

                                    }}
                                >
                                    {snackbarMessage}
                                </Alert>
                            </Snackbar>
                        </Box>
                    </Box>
                </Dialog>


                <Snackbar
                    open={alertOpen}
                    autoHideDuration={6000}
                    onClose={() => setAlertOpen(false)}
                    message="Saved successfully!"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                />
            </Box>

            <Dialog
                open={editOpen}
                onClose={handleEditClose}
                TransitionComponent={Transition}
                maxWidth="md" // or "lg" for larger width
                fullWidth
            >
                <AppBar sx={{ position: 'relative', bgcolor: '#FAFAFA', boxShadow: 'none' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1, color: 'black', fontWeight: 'bold' }} variant="h6" component="div">
                            Edit Payment
                        </Typography>
                        <IconButton edge="end" color="inherit" onClick={handleEditClose} aria-label="close">
                            <CloseIcon sx={{ color: 'red' }} />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <Box sx={{ p: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row', // Set to row to arrange inputs horizontally
                            alignItems: 'center', // Align items vertically centered
                            justifyContent: 'space-between', // Space between inputs
                            marginTop: '10px',
                            textAlign: 'right',
                            gap: 1
                        }}
                    >
                        <TextField
                            label="Payment Amount"
                            type="number"
                            value={editPayment.amount}
                            size="small"
                            onChange={handleEditAmountChange}
                            sx={{ marginBottom: '10px', width: '48%' }} // Adjust width to fit two inputs in a row
                        />
                        <TextField
                            label="Memo"
                            type="text"
                            value={editPayment.memo}
                            size="small"
                            onChange={handleEditMemoChange}
                            sx={{ marginBottom: '10px', width: '48%' }} // Adjust width to fit two inputs in a row
                        />
                        <TextField
                            label="Payment Date"
                            type="date"
                            value={editPayment.date}
                            size="small"
                            onChange={handleEditDateChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{ marginBottom: '10px', width: '48%' }} // Adjust width to fit two inputs in a row
                        />
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            marginTop: '10px',
                            textAlign: 'right'
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleEditSave}
                            color="primary"
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
                {loading && (
                    <Box
                        sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'white',
                            zIndex: 1300,
                            pointerEvents: 'auto'
                        }}
                    >
                        <l-trio size="50" speed="1.3" color="red"></l-trio>
                    </Box>
                )}
            </Dialog>
        </React.Fragment>
    );
};

export default MakePayment;
